import { memo, useCallback, useContext } from 'react';

import PersonInPlaceEditor from '@/components/PersonInPlaceEditor';
import { FieldTypes } from '@/components/PersonInPlaceEditor/types';
import useLeadSourceChoices from '@/hooks/useLeadSourceChoices';
import useNavigateToPage from '@/hooks/useNavigateToPage';
import PersonContext from '@/pages/Person/PersonContext';
import Button from '@/ui/components/Button';
import DataList, { DataRow } from '@/ui/components/DataList';
import Spinner from '@/ui/components/Spinner';

import useReferralsForPersonQuery from '../../../../hooks/useReferralsForPersonQuery';
import ReferralRow from '../ReferralRow';

const Referrals = () => {
  const {
    person: { id: personId, referrer_name, user_identified_lead_source, time_zone: personTimeZone }
  } = useContext(PersonContext);

  const { navigateToNewReferral } = useNavigateToPage();

  const { refetch: getLeadSources } = useLeadSourceChoices(false);
  const formatLeadSources = useCallback(async () => {
    const { data: leadSources } = await getLeadSources();
    if (leadSources) return leadSources.map(({ id, name }) => ({ label: name, value: id }));
    return [];
  }, [getLeadSources]);

  const inboundReferralsQuery = useReferralsForPersonQuery(true, { referred_by_id: personId });
  const referralsQuery = useReferralsForPersonQuery(true, { person_id: personId });

  const isPending = referralsQuery.isPending || inboundReferralsQuery.isPending;
  const isError = referralsQuery.isError || inboundReferralsQuery.isError;

  if (isPending) {
    return <Spinner />;
  }

  if (isError) {
    return <div>Referrals Could not be loaded</div>;
  }

  const referrals = referralsQuery.data?.data || [];
  const inbound_referrals = inboundReferralsQuery.data?.data || [];

  return (
    <div>
      <DataRow label="How did you hear about us?">
        <PersonInPlaceEditor
          fields={[
            {
              key: 'user_identified_lead_source_id',
              label: 'How did you hear about us?',
              type: FieldTypes.SELECT,
              options: formatLeadSources
            }
          ]}>
          {user_identified_lead_source?.name || 'Not set'}
        </PersonInPlaceEditor>
      </DataRow>

      {!!referrer_name && (
        <DataRow label="Referrer Name (Mobile Onboarding)">{referrer_name}</DataRow>
      )}

      {!referrals.length && !inbound_referrals.length && <div>No Referrals</div>}

      {referrals.length > 0 && (
        <DataList>
          {referrals.map((referral, idx, arr) => {
            return (
              <ReferralRow
                key={referral.id}
                referral={referral}
                personTimeZone={personTimeZone}
                pos={arr.length > 1 ? idx + 1 : idx}
              />
            );
          })}
        </DataList>
      )}

      {inbound_referrals.length > 0 && (
        <DataList>
          {inbound_referrals.map((referral, idx, arr) => {
            return (
              <ReferralRow
                inbound
                key={referral.id}
                referral={referral}
                personTimeZone={personTimeZone}
                pos={arr.length > 1 ? idx + 1 : idx}
              />
            );
          })}
        </DataList>
      )}

      <Button
        className="mt-4"
        onClick={() => {
          navigateToNewReferral(personId);
        }}>
        Add Referral
      </Button>
    </div>
  );
};
export default memo(Referrals);
